import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { authAPI, mfaAPI, usersAPI } from '../services/api';
import pushNotificationService from '../services/pushNotificationService';
import CustomSelect from '../components/common/CustomSelect';
import TextInput from '../components/common/TextInput';
import Toggle from '../components/common/Toggle';
import PasswordRequirements, { usePasswordRequirements } from '../components/PasswordRequirements';
import ActiveSessions from '../components/settings/ActiveSessions';
import ConnectedAccounts from '../components/settings/ConnectedAccounts';
import IncomeSettings from '../components/settings/IncomeSettings';
import { useIsMobile } from '../hooks';
import { 
  User,
  Palette,
  Bell,
  Shield,
  Sun,
  Moon,
  Upload,
  X,
  ChevronDown,
  ChevronRight,
  Smartphone,
  Key,
  Copy,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  Download,
  FileJson,
  Table,
  BellRing,
  BellOff,
  Lock,
  Database,
  Wallet
} from 'lucide-react';
import '../styles/Settings.css';

function Settings({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Valid tab IDs - 7 focused tabs like admin settings
  const validTabs = ['profile', 'appearance', 'notifications', 'income', 'password', 'security', 'data'];
  
  // Initialize tab from URL or default to 'profile'
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromUrl = searchParams.get('tab');
    return validTabs.includes(tabFromUrl) ? tabFromUrl : 'profile';
  });

  const isMobile = useIsMobile(768);
  // On mobile, start with menu open only if no tab is specified in URL
  const [mobileMenuOpen, setMobileMenuOpen] = useState(() => {
    const urlTab = searchParams.get('tab');
    return !urlTab;
  });

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'appearance', label: 'Appearance', icon: Palette },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'income', label: 'Income', icon: Wallet },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'data', label: 'Data', icon: Database },
  ];

  // Sync URL when tab changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (currentTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, searchParams, setSearchParams]);

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (isMobile) {
      setMobileMenuOpen(false); // Hide menu, show content
    }
    // Scroll to top of content
    const contentArea = document.querySelector('.sidebar-layout__content');
    if (contentArea) {
      contentArea.scrollTop = 0;
    }
  };

  const handleMobileBack = () => {
    setMobileMenuOpen(true); // Show menu again
    // Clear the tab from URL so refresh stays on menu
    setSearchParams({}, { replace: true });
  };

  // Render the tab content (shared between mobile and desktop)
  const renderContent = () => (
    <>
      {activeTab === 'profile' && <ProfileSettings user={user} />}
      {activeTab === 'appearance' && <AppearanceSettings />}
      {activeTab === 'notifications' && <NotificationSettings user={user} />}
      {activeTab === 'income' && <IncomeSettings user={user} />}
      {activeTab === 'password' && <PasswordSettings />}
      {activeTab === 'security' && <SecuritySettings />}
      {activeTab === 'data' && <DataSettings user={user} />}
    </>
  );

  return (
    <div className="page settings-page">
      <div className={`sidebar-layout ${isMobile ? 'sidebar-layout--mobile-menu' : ''}`}>
        {isMobile ? (
          /* Mobile: Full Page List Pattern */
          mobileMenuOpen ? (
            /* Menu List */
            <div className="sidebar-layout__mobile-menu">
              <div className="sidebar-layout__mobile-header">
                <h2>Settings</h2>
              </div>
              <div className="sidebar-layout__mobile-list">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      className={`sidebar-layout__mobile-item ${activeTab === tab.id ? 'active' : ''}`}
                      onClick={() => handleTabChange(tab.id)}
                    >
                      <Icon size={20} />
                      <span>{tab.label}</span>
                      <ChevronRight size={18} className="chevron" />
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Content View */
            <div className="sidebar-layout__mobile-content">
              <button className="sidebar-layout__mobile-back" onClick={handleMobileBack}>
                <ChevronRight size={16} className="back-icon" />
                <span>Settings</span>
              </button>
              <div className="sidebar-layout__content settings-content">
                {renderContent()}
              </div>
            </div>
          )
        ) : (
          /* Desktop Layout */
          <>
            {/* Sidebar Tabs */}
            <div className="sidebar-layout__nav">
              <div className="sidebar-layout__nav-inner">
                <nav className="nav-tabs nav-tabs--vertical nav-tabs--settings">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                        onClick={() => handleTabChange(tab.id)}
                      >
                        <Icon size={20} />
                        <span className="nav-tab__label">{tab.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Content Area */}
            <div className="sidebar-layout__content settings-content">
              {renderContent()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProfileSettings({ user }) {
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName: user?.last_name || '',
    email: user?.email || '',
    timezone: user?.timezone || 'America/Chicago'
  });
  // eslint-disable-next-line no-unused-vars
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    user?.preferences?.profile_image || localStorage.getItem('profile_image') || null
  );
  const [loading, setLoading] = useState(false);
  const [detectingTimezone, setDetectingTimezone] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Auto-detect timezone on mount if not set
  useEffect(() => {
    if (!user?.timezone) {
      detectTimezone(false); // Silent detection on load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const detectTimezone = async (showMessage = true) => {
    setDetectingTimezone(true);
    try {
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      const timezoneMap = {
        'America/New_York': 'America/New_York',
        'America/Detroit': 'America/New_York',
        'America/Indiana/Indianapolis': 'America/New_York',
        'America/Chicago': 'America/Chicago',
        'America/Menominee': 'America/Chicago',
        'America/Denver': 'America/Denver',
        'America/Boise': 'America/Denver',
        'America/Phoenix': 'America/Denver',
        'America/Los_Angeles': 'America/Los_Angeles',
        'America/Anchorage': 'America/Anchorage',
        'America/Juneau': 'America/Anchorage',
        'Pacific/Honolulu': 'Pacific/Honolulu',
        'UTC': 'UTC'
      };

      let detectedZone = timezoneMap[browserTimezone];
      
      if (!detectedZone) {
        if (browserTimezone.startsWith('America/')) {
          const now = new Date();
          const offset = -now.getTimezoneOffset() / 60;
          
          if (offset >= -5 && offset <= -4) detectedZone = 'America/New_York';
          else if (offset >= -6 && offset <= -5) detectedZone = 'America/Chicago';
          else if (offset >= -7 && offset <= -6) detectedZone = 'America/Denver';
          else if (offset >= -8 && offset <= -7) detectedZone = 'America/Los_Angeles';
          else if (offset >= -9 && offset <= -8) detectedZone = 'America/Anchorage';
          else if (offset === -10) detectedZone = 'Pacific/Honolulu';
          else detectedZone = 'UTC';
        } else {
          detectedZone = 'UTC';
        }
      }

      setFormData(prev => ({ ...prev, timezone: detectedZone }));
      if (showMessage) {
        setMessage({ type: 'success', text: `Timezone detected: ${getTimezoneLabel(detectedZone)}` });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      }
    } catch (error) {
      console.error('Timezone detection error:', error);
      if (showMessage) {
        setMessage({ type: 'error', text: 'Could not detect timezone automatically' });
      }
    } finally {
      setDetectingTimezone(false);
    }
  };

  const getTimezoneLabel = (tz) => {
    const labels = {
      'America/New_York': 'Eastern Time',
      'America/Chicago': 'Central Time',
      'America/Denver': 'Mountain Time',
      'America/Los_Angeles': 'Pacific Time',
      'America/Anchorage': 'Alaska Time',
      'Pacific/Honolulu': 'Hawaii Time',
      'UTC': 'UTC'
    };
    return labels[tz] || tz;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'Image must be less than 5MB' });
        return;
      }

      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'File must be an image' });
        return;
      }

      setProfileImage(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setProfileImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedPreferences = {
        ...currentUser.preferences,
        profile_image: imagePreview
      };

      await authAPI.updatePreferences(updatedPreferences);

      await authAPI.updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        timezone: formData.timezone
      });

      if (imagePreview) {
        localStorage.setItem('profile_image', imagePreview);
      } else {
        localStorage.removeItem('profile_image');
      }

      const updatedUser = {
        ...currentUser,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        timezone: formData.timezone,
        preferences: updatedPreferences
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('Update profile error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to update profile' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getInitials = () => {
    const first = formData.firstName?.[0] || '';
    const last = formData.lastName?.[0] || '';
    return (first + last).toUpperCase();
  };

  return (
    <div className="settings-section">
      <h2>Profile Settings</h2>
      <p className="section-description">Update your personal information and profile picture</p>
      
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-form">
        <div className="form-group profile-picture-section">
          <label>Profile Picture</label>
          <div className="profile-picture-container">
            <div className="profile-picture-preview">
              {imagePreview ? (
                <img src={imagePreview} alt="Profile" className="profile-image" />
              ) : (
                <div className="profile-avatar-large">
                  {getInitials()}
                </div>
              )}
            </div>
            <div className="profile-picture-actions">
              <label htmlFor="profile-image-upload" className="btn-secondary">
                <Upload size={18} />
                {imagePreview ? 'Change Photo' : 'Upload Photo'}
              </label>
              <input
                type="file"
                id="profile-image-upload"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
                disabled={loading}
              />
              {imagePreview && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="btn-danger-outline"
                  disabled={loading}
                >
                  <X size={18} />
                  Remove
                </button>
              )}
            </div>
          </div>
          <small className="form-hint">JPG, PNG or GIF. Max size 5MB.</small>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <TextInput
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <TextInput
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <TextInput
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            disabled={loading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="timezone">Timezone</label>
          <div className="timezone-select-container">
            <CustomSelect
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              disabled={loading || detectingTimezone}
              options={[
                { value: 'America/New_York', label: 'Eastern Time (ET)', icon: '🌎' },
                { value: 'America/Chicago', label: 'Central Time (CT)', icon: '🌎' },
                { value: 'America/Denver', label: 'Mountain Time (MT)', icon: '🌎' },
                { value: 'America/Los_Angeles', label: 'Pacific Time (PT)', icon: '🌎' },
                { value: 'America/Anchorage', label: 'Alaska Time (AKT)', icon: '🌎' },
                { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)', icon: '🌴' },
                { value: 'UTC', label: 'UTC', icon: '🌐' }
              ]}
            />
            <button
              type="button"
              className="btn-secondary btn-detect-timezone"
              onClick={() => detectTimezone(true)}
              disabled={loading || detectingTimezone}
              title="Detect my timezone"
            >
              {detectingTimezone ? '🔄' : '📍'} {detectingTimezone ? 'Detecting...' : 'Detect'}
            </button>
          </div>
          <small className="form-hint">
            Daylight saving time adjustments are automatic. Click "Detect" to use your device's timezone.
          </small>
        </div>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

function AppearanceSettings() {
  const { 
    mode, 
    toggleMode, 
    accentColor, 
    updateAccentColor,
    darkBackground,
    updateDarkBackground,
    accentColors,
    darkBackgrounds 
  } = useTheme();

  return (
    <div className="settings-section">
      <h2>Appearance</h2>
      <p className="section-description">Customize how Bill Tracker looks for you</p>
      
      <div className="settings-group">
        <h3>Theme Mode</h3>
        <div className="theme-mode-selector">
          <button 
            className={`mode-option ${mode === 'light' ? 'active' : ''}`}
            onClick={() => mode !== 'light' && toggleMode()}
          >
            <span className="mode-icon">
              <Sun size={32} />
            </span>
            <span>Light Mode</span>
          </button>
          <button 
            className={`mode-option ${mode === 'dark' ? 'active' : ''}`}
            onClick={() => mode !== 'dark' && toggleMode()}
          >
            <span className="mode-icon">
              <Moon size={32} />
            </span>
            <span>Dark Mode</span>
          </button>
        </div>
      </div>

      <div className="settings-group">
        <h3>Accent Color</h3>
        <p className="setting-description">Choose your preferred accent color</p>
        <div className="color-grid">
          {accentColors.map((color) => (
            <button
              key={color.name}
              className={`color-option ${accentColor.name === color.name ? 'active' : ''}`}
              onClick={() => updateAccentColor(color)}
              title={color.name}
            >
              <div 
                className="color-swatch" 
                style={{ backgroundColor: color.value }}
              >
                {accentColor.name === color.name && (
                  <span className="checkmark">✓</span>
                )}
              </div>
              <span className="color-name">{color.name}</span>
            </button>
          ))}
        </div>
      </div>

      {mode === 'dark' && (
        <div className="settings-group">
          <h3>Dark Background</h3>
          <p className="setting-description">Choose your preferred dark mode background</p>
          <div className="background-options">
            {darkBackgrounds.map((bg) => (
              <button
                key={bg.name}
                className={`background-option ${darkBackground.name === bg.name ? 'active' : ''}`}
                onClick={() => updateDarkBackground(bg)}
              >
                <div className="background-preview">
                  <div 
                    className="bg-sample bg" 
                    style={{ backgroundColor: bg.bg }}
                  />
                  <div 
                    className="bg-sample surface" 
                    style={{ backgroundColor: bg.surface }}
                  />
                  <div 
                    className="bg-sample border" 
                    style={{ backgroundColor: bg.border }}
                  />
                </div>
                <div className="background-info">
                  <span className="background-name">{bg.name}</span>
                  {darkBackground.name === bg.name && (
                    <span className="active-badge">Active</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CollapsibleSection({ title, description, children, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`collapsible-section ${isOpen ? 'open' : ''}`}>
      <button 
        type="button"
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="collapsible-title-area">
          <h3>{title}</h3>
          {description && <p className="setting-description">{description}</p>}
        </div>
        <ChevronDown 
          size={20} 
          className={`collapsible-icon ${isOpen ? 'rotated' : ''}`}
        />
      </button>
      <div className={`collapsible-content ${isOpen ? 'expanded' : ''}`}>
        {children}
      </div>
    </div>
  );
}

function NotificationSettings({ user }) {
  const { success: showSuccess, error: showError } = useToast();
  const [formData, setFormData] = useState({
    emailNotifications: user?.preferences?.notifications?.emailEnabled ?? true,
    pushNotifications: user?.preferences?.notifications?.pushEnabled ?? false,
    billReminders: user?.preferences?.notifications?.billReminders ?? true,
    reminderDaysBefore: user?.preferences?.notifications?.reminderDaysBefore ?? 3,
    overdueAlerts: user?.preferences?.notifications?.overdueAlerts ?? true,
    weeklyDigest: user?.preferences?.notifications?.weeklyDigest ?? false,
    monthlyReport: user?.preferences?.notifications?.monthlyReport ?? true,
    paymentConfirmations: user?.preferences?.notifications?.paymentConfirmations ?? true,
    householdUpdates: user?.preferences?.notifications?.householdUpdates ?? true
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const testButtonRef = useRef(null);
  const testDropdownRef = useRef(null);

  useEffect(() => {
    if (showTestDropdown && testButtonRef.current) {
      const rect = testButtonRef.current.getBoundingClientRect();
      const dropdownHeight = 320;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      
      const openUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      
      setDropdownPosition({
        top: openUpward ? null : rect.bottom + 4,
        bottom: openUpward ? (window.innerHeight - rect.top + 4) : null,
        right: window.innerWidth - rect.right,
        openUpward
      });
    }
  }, [showTestDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        testDropdownRef.current && 
        !testDropdownRef.current.contains(event.target) &&
        testButtonRef.current &&
        !testButtonRef.current.contains(event.target)
      ) {
        setShowTestDropdown(false);
      }
    };

    if (showTestDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTestDropdown]);

  useEffect(() => {
    checkPushSupport();
  }, []);

  const checkPushSupport = async () => {
    const supported = pushNotificationService.isSupported();
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(pushNotificationService.getPermissionStatus());
      const browserSubscribed = await pushNotificationService.isSubscribed();
      
      let serverEnabled = false;
      try {
        const status = await pushNotificationService.getStatus();
        serverEnabled = status.enabled;
        setPushEnabled(serverEnabled);
      } catch (e) {
        try {
          const initialized = await pushNotificationService.initialize();
          serverEnabled = initialized;
          setPushEnabled(initialized);
        } catch {
          setPushEnabled(false);
        }
      }
      
      if (browserSubscribed && serverEnabled) {
        await pushNotificationService.syncSubscription();
      }
      
      setPushSubscribed(browserSubscribed);
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      await pushNotificationService.subscribe();
      setPushSubscribed(true);
      setPushPermission('granted');
      setFormData(prev => ({ ...prev, pushNotifications: true }));
      showSuccess('Push notifications enabled!');
    } catch (error) {
      console.error('Failed to enable push:', error);
      if (error.message === 'Notification permission denied') {
        showError('Please allow notifications in your browser settings');
        setPushPermission('denied');
      } else {
        showError('Failed to enable push notifications');
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      await pushNotificationService.unsubscribe();
      setPushSubscribed(false);
      setFormData(prev => ({ ...prev, pushNotifications: false }));
      showSuccess('Push notifications disabled');
    } catch (error) {
      console.error('Failed to disable push:', error);
      showError('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async (type = 'default') => {
    setPushLoading(true);
    setShowTestDropdown(false);
    try {
      if (type === 'default') {
        await pushNotificationService.sendTest();
      } else {
        await pushNotificationService.sendTestByType(type);
      }
      const testTypes = pushNotificationService.getTestTypes();
      const selectedType = testTypes.find(t => t.value === type);
      showSuccess(`${selectedType?.icon || '🔔'} ${selectedType?.label || 'Test'} notification sent!`);
    } catch (error) {
      console.error('Failed to send test:', error);
      showError(error.response?.data?.message || 'Failed to send test notification');
    } finally {
      setPushLoading(false);
    }
  };

  const notificationTestTypes = pushNotificationService.getTestTypes();

  const handleToggle = (field) => {
    setFormData({
      ...formData,
      [field]: !formData[field]
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedPreferences = {
        ...currentUser.preferences,
        notifications: {
          emailEnabled: formData.emailNotifications,
          pushEnabled: formData.pushNotifications,
          billReminders: formData.billReminders,
          reminderDaysBefore: formData.reminderDaysBefore,
          overdueAlerts: formData.overdueAlerts,
          weeklyDigest: formData.weeklyDigest,
          monthlyReport: formData.monthlyReport,
          paymentConfirmations: formData.paymentConfirmations,
          householdUpdates: formData.householdUpdates
        }
      };

      await authAPI.updatePreferences(updatedPreferences);

      const updatedUser = {
        ...currentUser,
        preferences: updatedPreferences
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      setMessage({ type: 'success', text: 'Notification preferences updated successfully!' });
    } catch (error) {
      console.error('Update notifications error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to update notification preferences' 
      });
    } finally {
      setLoading(false);
    }
  };

  const getPushStatusText = () => {
    if (!pushSupported) return 'Not supported in this browser';
    if (!pushEnabled) return 'Not configured on server';
    if (pushPermission === 'denied') return 'Blocked by browser';
    if (pushSubscribed) return 'Enabled';
    return 'Disabled';
  };

  const getPushStatusColor = () => {
    if (!pushSupported || !pushEnabled || pushPermission === 'denied') return 'gray';
    if (pushSubscribed) return 'green';
    return 'gray';
  };

  return (
    <div className="settings-section">
      <h2>Notification Preferences</h2>
      <p className="section-description">Manage how and when you receive notifications</p>
      
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="notifications-form">
        <CollapsibleSection 
          title="Notification Channels" 
          description="Choose how you want to receive notifications"
          defaultOpen={true}
        >
          <div className="toggle-group">
            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Email Notifications</span>
                <p className="toggle-description">Receive notifications via email</p>
              </div>
              <Toggle
                checked={formData.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
                disabled={loading}
              />
            </div>

            <div className="toggle-item push-notification-item">
              <div className="toggle-info">
                <span className="toggle-label">
                  Push Notifications
                  <span className={`push-status-badge ${getPushStatusColor()}`}>
                    {getPushStatusText()}
                  </span>
                </span>
                <p className="toggle-description">Receive browser push notifications</p>
              </div>
              
              {pushSupported && pushEnabled ? (
                <div className="push-actions">
                  {pushSubscribed ? (
                    <>
                      <div className="test-notification-dropdown">
                        <button
                          ref={testButtonRef}
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => setShowTestDropdown(!showTestDropdown)}
                          disabled={pushLoading}
                        >
                          {pushLoading ? <Loader2 size={14} className="spinner" /> : <BellRing size={14} />}
                          Test
                          <ChevronDown size={14} className={showTestDropdown ? 'rotated' : ''} />
                        </button>
                        {showTestDropdown && createPortal(
                          <div 
                            ref={testDropdownRef}
                            className={`test-dropdown-menu ${dropdownPosition.openUpward ? 'open-upward' : 'open-downward'}`}
                            style={{
                              position: 'fixed',
                              top: dropdownPosition.top != null ? `${dropdownPosition.top}px` : 'auto',
                              bottom: dropdownPosition.bottom != null ? `${dropdownPosition.bottom}px` : 'auto',
                              right: `${dropdownPosition.right}px`
                            }}
                          >
                            {notificationTestTypes.map((type) => (
                              <button
                                key={type.value}
                                type="button"
                                className="test-dropdown-item"
                                onClick={() => handleTestPush(type.value)}
                              >
                                <span className="test-icon">{type.icon}</span>
                                <div className="test-info">
                                  <span className="test-label">{type.label}</span>
                                  <span className="test-desc">{type.description}</span>
                                </div>
                              </button>
                            ))}
                          </div>,
                          document.body
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn-danger-outline btn-sm"
                        onClick={handleDisablePush}
                        disabled={pushLoading}
                      >
                        <BellOff size={14} />
                        Disable
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={handleEnablePush}
                      disabled={pushLoading || pushPermission === 'denied'}
                    >
                      {pushLoading ? <Loader2 size={14} className="spinner" /> : <Bell size={14} />}
                      Enable
                    </button>
                  )}
                </div>
              ) : (
                <Toggle
                  checked={false}
                  onChange={() => {}}
                  disabled={true}
                />
              )}
            </div>
            
            {pushPermission === 'denied' && (
              <div className="push-blocked-hint">
                <AlertTriangle size={14} />
                <span>Push notifications are blocked. Please enable them in your browser settings.</span>
              </div>
            )}
          </div>
        </CollapsibleSection>

        <CollapsibleSection 
          title="Bill Reminders" 
          description="Get reminded about upcoming bills"
        >
          <div className="toggle-group">
            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Bill Reminders</span>
                <p className="toggle-description">Receive reminders before bills are due</p>
              </div>
              <Toggle
                checked={formData.billReminders}
                onChange={() => handleToggle('billReminders')}
                disabled={loading}
              />
            </div>
          </div>

          {formData.billReminders && (
            <div className="form-group" style={{ marginTop: 'var(--spacing-md)' }}>
              <label htmlFor="reminderDaysBefore">Remind me this many days before due date</label>
              <CustomSelect
                name="reminderDaysBefore"
                value={formData.reminderDaysBefore.toString()}
                onChange={(e) => setFormData({ ...formData, reminderDaysBefore: parseInt(e.target.value) })}
                disabled={loading}
                options={[
                  { value: '1', label: '1 day before', icon: '📅' },
                  { value: '2', label: '2 days before', icon: '📅' },
                  { value: '3', label: '3 days before', icon: '📅' },
                  { value: '5', label: '5 days before', icon: '📅' },
                  { value: '7', label: '1 week before', icon: '📆' },
                  { value: '14', label: '2 weeks before', icon: '📆' }
                ]}
              />
            </div>
          )}

          <div className="toggle-group" style={{ marginTop: 'var(--spacing-md)' }}>
            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Overdue Alerts</span>
                <p className="toggle-description">Get notified when bills become overdue</p>
              </div>
              <Toggle
                checked={formData.overdueAlerts}
                onChange={() => handleToggle('overdueAlerts')}
                disabled={loading}
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection 
          title="Reports & Summaries" 
          description="Receive periodic summaries of your bills"
        >
          <div className="toggle-group">
            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Weekly Digest</span>
                <p className="toggle-description">Get a summary of upcoming bills every week</p>
              </div>
              <Toggle
                checked={formData.weeklyDigest}
                onChange={() => handleToggle('weeklyDigest')}
                disabled={loading}
              />
            </div>

            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Monthly Report</span>
                <p className="toggle-description">Receive a monthly spending report</p>
              </div>
              <Toggle
                checked={formData.monthlyReport}
                onChange={() => handleToggle('monthlyReport')}
                disabled={loading}
              />
            </div>
          </div>
        </CollapsibleSection>

        <CollapsibleSection 
          title="Activity Notifications" 
          description="Stay updated on payment activity"
        >
          <div className="toggle-group">
            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Payment Confirmations</span>
                <p className="toggle-description">Get notified when bills are marked as paid</p>
              </div>
              <Toggle
                checked={formData.paymentConfirmations}
                onChange={() => handleToggle('paymentConfirmations')}
                disabled={loading}
              />
            </div>

            <div className="toggle-item">
              <div className="toggle-info">
                <span className="toggle-label">Household Updates</span>
                <p className="toggle-description">Get notified of changes to household settings or members</p>
              </div>
              <Toggle
                checked={formData.householdUpdates}
                onChange={() => handleToggle('householdUpdates')}
                disabled={loading}
              />
            </div>
          </div>
        </CollapsibleSection>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PasswordSettings() {
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const { isPasswordValid } = usePasswordRequirements();

  const doPasswordsMatch = () => {
    if (!passwordData.confirmPassword) return true;
    return passwordData.newPassword === passwordData.confirmPassword;
  };

  const canSubmit = () => {
    return passwordData.currentPassword && 
           passwordData.newPassword && 
           passwordData.confirmPassword &&
           isPasswordValid(passwordData.newPassword) && 
           doPasswordsMatch();
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (!isPasswordValid(passwordData.newPassword)) {
      setMessage({ type: 'error', text: 'Password does not meet requirements' });
      setLoading(false);
      return;
    }

    try {
      await authAPI.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      console.error('Update password error:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to update password' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <h2>Change Password</h2>
      <p className="section-description">Update your password to keep your account secure</p>
      
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="password-change-layout">
        <form onSubmit={handlePasswordSubmit} className="password-form">
          <div className="form-group">
            <label htmlFor="currentPassword">Current Password</label>
            <div className="password-input-wrapper">
              <TextInput
                type={showPasswords.current ? 'text' : 'password'}
                id="currentPassword"
                name="currentPassword"
                value={passwordData.currentPassword}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                disabled={loading}
                title={showPasswords.current ? 'Hide password' : 'Show password'}
              >
                {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="newPassword">New Password</label>
            <div className="password-input-wrapper">
              <TextInput
                type={showPasswords.new ? 'text' : 'password'}
                id="newPassword"
                name="newPassword"
                value={passwordData.newPassword}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                disabled={loading}
                title={showPasswords.new ? 'Hide password' : 'Show password'}
              >
                {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="password-requirements-mobile">
            <PasswordRequirements password={passwordData.newPassword} />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <div className="password-input-wrapper">
              <TextInput
                type={showPasswords.confirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                value={passwordData.confirmPassword}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                autoComplete="new-password"
                className={
                  passwordData.confirmPassword 
                    ? (doPasswordsMatch() ? 'input-success' : 'input-error')
                    : ''
                }
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                disabled={loading}
                title={showPasswords.confirm ? 'Hide password' : 'Show password'}
              >
                {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {passwordData.confirmPassword && (
              <small className={doPasswordsMatch() ? 'success-text' : 'error-text'}>
                {doPasswordsMatch() ? '✓ Passwords match' : 'Passwords do not match'}
              </small>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={loading || !canSubmit()}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </div>
        </form>

        <div className="password-requirements-desktop">
          <PasswordRequirements password={passwordData.newPassword} />
        </div>
      </div>
    </div>
  );
}

function SecuritySettings() {
  return (
    <div className="settings-section">
      <h2>Security</h2>
      <p className="section-description">Manage two-factor authentication, active sessions, and connected accounts</p>
      
      <MFASettings />
      <ConnectedAccounts />
      <ActiveSessions />
    </div>
  );
}

function MFASettings() {
  const [mfaStatus, setMfaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disabling, setDisabling] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratePassword, setRegeneratePassword] = useState('');
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [copiedCode, setCopiedCode] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    loadMFAStatus();
  }, []);

  const loadMFAStatus = async () => {
    setLoading(true);
    try {
      const response = await mfaAPI.getStatus();
      setMfaStatus(response.data);
    } catch (error) {
      console.error('Failed to load MFA status:', error);
      setMessage({ type: 'error', text: 'Failed to load MFA status' });
    } finally {
      setLoading(false);
    }
  };

  const handleStartSetup = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await mfaAPI.setup();
      setSetupData(response.data);
    } catch (error) {
      console.error('Failed to start MFA setup:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to start MFA setup' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndEnable = async (e) => {
    e.preventDefault();
    if (verificationCode.length !== 6) {
      setMessage({ type: 'error', text: 'Please enter a 6-digit code' });
      return;
    }

    setVerifying(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await mfaAPI.enable(verificationCode);
      setBackupCodes(response.data.backupCodes);
      setShowBackupCodes(true);
      setSetupData(null);
      setVerificationCode('');
      await loadMFAStatus();
      setMessage({ type: 'success', text: 'Two-factor authentication enabled successfully!' });
    } catch (error) {
      console.error('Failed to verify MFA:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Invalid verification code' });
    } finally {
      setVerifying(false);
    }
  };

  const handleDisableMFA = async (e) => {
    e.preventDefault();
    if (!disablePassword) {
      setMessage({ type: 'error', text: 'Please enter your password' });
      return;
    }

    setDisabling(true);
    setMessage({ type: '', text: '' });
    try {
      await mfaAPI.disable(disablePassword);
      setShowDisableConfirm(false);
      setDisablePassword('');
      await loadMFAStatus();
      setMessage({ type: 'success', text: 'Two-factor authentication disabled' });
    } catch (error) {
      console.error('Failed to disable MFA:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to disable MFA' });
    } finally {
      setDisabling(false);
    }
  };

  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    if (!regeneratePassword) {
      setMessage({ type: 'error', text: 'Please enter your password' });
      return;
    }

    setRegenerating(true);
    setMessage({ type: '', text: '' });
    try {
      const response = await mfaAPI.regenerateBackupCodes(regeneratePassword);
      setBackupCodes(response.data.backupCodes);
      setShowBackupCodes(true);
      setShowRegenerateConfirm(false);
      setRegeneratePassword('');
      await loadMFAStatus();
      setMessage({ type: 'success', text: 'New backup codes generated!' });
    } catch (error) {
      console.error('Failed to regenerate backup codes:', error);
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to regenerate backup codes' });
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCode(index);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const copyAllBackupCodes = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join('\n'));
      setMessage({ type: 'success', text: 'All backup codes copied to clipboard' });
      setTimeout(() => setMessage({ type: '', text: '' }), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const cancelSetup = () => {
    setSetupData(null);
    setVerificationCode('');
    setMessage({ type: '', text: '' });
  };

  if (loading && !setupData) {
    return (
      <div className="settings-group">
        <h3>Two-Factor Authentication</h3>
        <div className="loading-state">
          <Loader2 size={24} className="spinner" />
          <span>Loading MFA status...</span>
        </div>
      </div>
    );
  }

  if (showBackupCodes && backupCodes) {
    return (
      <div className="settings-group">
        <h3>Two-Factor Authentication</h3>
        
        <div className="mfa-backup-codes-display">
          <div className="backup-codes-header">
            <AlertTriangle size={24} className="warning-icon" />
            <div>
              <h4>Save Your Backup Codes</h4>
              <p>Store these codes in a safe place. Each code can only be used once.</p>
            </div>
          </div>
          
          <div className="backup-codes-grid">
            {backupCodes.map((code, index) => (
              <div key={index} className="backup-code-item">
                <code>{code}</code>
                <button
                  type="button"
                  className="btn-icon-sm"
                  onClick={() => copyToClipboard(code, index)}
                  title="Copy code"
                >
                  {copiedCode === index ? <CheckCircle size={16} /> : <Copy size={16} />}
                </button>
              </div>
            ))}
          </div>
          
          <div className="backup-codes-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={copyAllBackupCodes}
            >
              <Copy size={16} />
              Copy All Codes
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => {
                setShowBackupCodes(false);
                setBackupCodes(null);
              }}
            >
              I've Saved My Codes
            </button>
          </div>
          
          <div className="backup-codes-warning">
            <AlertTriangle size={16} />
            <span>These codes will not be shown again. Make sure to save them now!</span>
          </div>
        </div>
      </div>
    );
  }

  if (setupData) {
    return (
      <div className="settings-group">
        <h3>Set Up Two-Factor Authentication</h3>
        <p className="setting-description">Scan the QR code with your authenticator app</p>
        
        {message.text && (
          <div className={`message message-${message.type}`}>
            {message.text}
          </div>
        )}
        
        <div className="mfa-setup-container">
          <div className="mfa-qr-section">
            <div className="qr-code-wrapper">
              <img src={setupData.qrCode} alt="QR Code for authenticator" />
            </div>
            <p className="qr-hint">Scan with Google Authenticator, Authy, or any TOTP app</p>
          </div>
          
          <div className="mfa-manual-entry">
            <h4>Can't scan? Enter manually:</h4>
            <div className="manual-key">
              <code>{setupData.manualEntry}</code>
              <button
                type="button"
                className="btn-icon-sm"
                onClick={() => copyToClipboard(setupData.manualEntry, 'manual')}
                title="Copy key"
              >
                {copiedCode === 'manual' ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
          </div>
          
          <form onSubmit={handleVerifyAndEnable} className="mfa-verify-form">
            <div className="form-group">
              <label htmlFor="verificationCode">Enter 6-digit code from your app</label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                disabled={verifying}
                className="mfa-code-input"
              />
            </div>
            
            <div className="mfa-setup-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={cancelSetup}
                disabled={verifying}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={verifying || verificationCode.length !== 6}
              >
                {verifying ? (
                  <><Loader2 size={16} className="spinner" /> Verifying...</>
                ) : (
                  <><CheckCircle size={16} /> Verify & Enable</>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-group">
      <h3>Two-Factor Authentication</h3>
      <p className="setting-description">Add an extra layer of security to your account</p>
      
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}
      
      {mfaStatus?.enabled ? (
        <div className="mfa-enabled-container">
          <div className="mfa-status-card enabled">
            <div className="mfa-status-icon">
              <Shield size={32} />
            </div>
            <div className="mfa-status-info">
              <h4>Two-Factor Authentication is Active</h4>
              <p>Your account is protected with an additional verification step</p>
              {mfaStatus.verifiedAt && (
                <span className="mfa-since">Enabled since {new Date(mfaStatus.verifiedAt).toLocaleDateString()}</span>
              )}
            </div>
            <span className="mfa-badge enabled">Enabled</span>
          </div>
          
          <div className="mfa-management">
            <div className="mfa-info-item">
              <div className="info-icon"><Key size={20} /></div>
              <div className="info-content">
                <h5>Backup Codes</h5>
                <p>{mfaStatus.backupCodesRemaining} of 10 codes remaining</p>
              </div>
              {!showRegenerateConfirm ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setShowRegenerateConfirm(true)}
                >
                  <RefreshCw size={14} />
                  Regenerate
                </button>
              ) : (
                <form onSubmit={handleRegenerateBackupCodes} className="inline-password-form">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={regeneratePassword}
                      onChange={(e) => setRegeneratePassword(e.target.value)}
                      placeholder="Enter password"
                      disabled={regenerating}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    className="btn-primary btn-sm"
                    disabled={regenerating || !regeneratePassword}
                  >
                    {regenerating ? <Loader2 size={14} className="spinner" /> : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      setShowRegenerateConfirm(false);
                      setRegeneratePassword('');
                    }}
                    disabled={regenerating}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
            
            <div className="mfa-info-item danger">
              <div className="info-icon"><AlertTriangle size={20} /></div>
              <div className="info-content">
                <h5>Disable 2FA</h5>
                <p>Remove two-factor authentication from your account</p>
              </div>
              {!showDisableConfirm ? (
                <button
                  type="button"
                  className="btn-danger-outline btn-sm"
                  onClick={() => setShowDisableConfirm(true)}
                >
                  Disable
                </button>
              ) : (
                <form onSubmit={handleDisableMFA} className="inline-password-form">
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={disablePassword}
                      onChange={(e) => setDisablePassword(e.target.value)}
                      placeholder="Enter password"
                      disabled={disabling}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <button
                    type="submit"
                    className="btn-danger btn-sm"
                    disabled={disabling || !disablePassword}
                  >
                    {disabling ? <Loader2 size={14} className="spinner" /> : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      setShowDisableConfirm(false);
                      setDisablePassword('');
                    }}
                    disabled={disabling}
                  >
                    Cancel
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="security-feature-card">
          <div className="feature-info">
            <div className="feature-icon">
              <Smartphone size={32} />
            </div>
            <div>
              <h4>Two-Factor Authentication (2FA)</h4>
              <p>Add an extra layer of security by requiring a code from your authenticator app when signing in</p>
            </div>
          </div>
          <button
            className="btn-primary"
            onClick={handleStartSetup}
            disabled={loading}
          >
            {loading ? (
              <><Loader2 size={16} className="spinner" /> Loading...</>
            ) : (
              <><Shield size={16} /> Enable 2FA</>
            )}
          </button>
        </div>
      )}
    </div>
  );
}

function DataSettings({ user }) {
  const { success: showSuccess, error: showError } = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportSummary, setExportSummary] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState('json');
  const [exportSelections, setExportSelections] = useState({
    user: true,
    bills: true,
    payments: true,
    paymentAccounts: true,
    categories: true,
    household: true
  });

  useEffect(() => {
    loadExportSummary();
  }, []);

  const loadExportSummary = async () => {
    try {
      const response = await usersAPI.getExportSummary();
      setExportSummary(response.data);
    } catch (error) {
      console.error('Failed to load export summary:', error);
    }
  };

  const handleDownloadData = async () => {
    setExportLoading(true);
    try {
      const response = await usersAPI.exportData();
      let data = response.data;
      
      const filteredData = {
        exportDate: data.exportDate,
        exportVersion: data.exportVersion,
        user: exportSelections.user ? data.user : null,
        bills: exportSelections.bills ? data.bills : [],
        payments: exportSelections.payments ? data.payments : [],
        paymentAccounts: exportSelections.paymentAccounts ? data.paymentAccounts : [],
        categories: exportSelections.categories ? data.categories : [],
        household: exportSelections.household ? data.household : null,
        notifications: exportSelections.user ? data.notifications : []
      };
      
      if (exportFormat === 'json') {
        const blob = new Blob([JSON.stringify(filteredData, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `remidy-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const csvFiles = convertToCSV(filteredData);
        downloadCSVFiles(csvFiles);
      }
      
      showSuccess('Data exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      console.error('Export data error:', error);
      showError('Failed to export data');
    } finally {
      setExportLoading(false);
    }
  };

  const toggleExportSelection = (key) => {
    setExportSelections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const selectAllExports = () => {
    setExportSelections({
      user: true,
      bills: true,
      payments: true,
      paymentAccounts: true,
      categories: true,
      household: true
    });
  };

  const deselectAllExports = () => {
    setExportSelections({
      user: false,
      bills: false,
      payments: false,
      paymentAccounts: false,
      categories: false,
      household: false
    });
  };

  const hasAnySelection = Object.values(exportSelections).some(v => v);

  const convertToCSV = (data) => {
    const csvFiles = {};
    
    const arrayToCSV = (arr) => {
      if (!arr || arr.length === 0) return null;
      
      const headers = Object.keys(arr[0]).filter(key => {
        const val = arr[0][key];
        return typeof val !== 'object' || val === null;
      });
      
      const csvRows = [
        headers.join(','),
        ...arr.map(row => 
          headers.map(header => {
            let val = row[header];
            if (val === null || val === undefined) return '';
            if (typeof val === 'string') {
              val = val.replace(/"/g, '""');
              if (val.includes(',') || val.includes('"') || val.includes('\n')) {
                val = `"${val}"`;
              }
            }
            return val;
          }).join(',')
        )
      ];
      
      return csvRows.join('\n');
    };
    
    if (data.user) {
      const userFlat = {
        id: data.user.id,
        email: data.user.email,
        first_name: data.user.first_name,
        last_name: data.user.last_name,
        role: data.user.role,
        household_role: data.user.household_role,
        timezone: data.user.timezone,
        is_active: data.user.is_active,
        mfa_enabled: data.user.mfa_enabled,
        created_at: data.user.created_at,
        updated_at: data.user.updated_at
      };
      csvFiles['user.csv'] = arrayToCSV([userFlat]);
    }
    
    if (data.bills?.length > 0) {
      csvFiles['bills.csv'] = arrayToCSV(data.bills);
    }
    
    if (data.payments?.length > 0) {
      csvFiles['payments.csv'] = arrayToCSV(data.payments);
    }
    
    if (data.paymentAccounts?.length > 0) {
      csvFiles['payment_accounts.csv'] = arrayToCSV(data.paymentAccounts);
    }
    
    if (data.categories?.length > 0) {
      csvFiles['categories.csv'] = arrayToCSV(data.categories);
    }
    
    if (data.household) {
      const householdFlat = {
        id: data.household.id,
        name: data.household.name,
        member_count: data.household.member_count,
        created_at: data.household.created_at,
        updated_at: data.household.updated_at
      };
      csvFiles['household.csv'] = arrayToCSV([householdFlat]);
      
      if (data.household.members?.length > 0) {
        csvFiles['household_members.csv'] = arrayToCSV(data.household.members);
      }
    }
    
    return csvFiles;
  };

  const downloadCSVFiles = (csvFiles) => {
    const fileNames = Object.keys(csvFiles).filter(k => csvFiles[k]);
    
    if (fileNames.length === 1) {
      const blob = new Blob([csvFiles[fileNames[0]]], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `remidy-${fileNames[0].replace('.csv', '')}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } else {
      fileNames.forEach((fileName, index) => {
        setTimeout(() => {
          const blob = new Blob([csvFiles[fileName]], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `remidy-${fileName.replace('.csv', '')}-${new Date().toISOString().split('T')[0]}.csv`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, index * 500);
      });
    }
  };

  return (
    <div className="settings-section">
      <h2>Your Data</h2>
      <p className="section-description">Export your data or delete your account</p>

      <div className="settings-group">
        <h3>Download Your Data</h3>
        <p className="setting-description">Export a copy of all your data stored in Remidy</p>
        
        <div className="privacy-actions">
          <div className="privacy-action-item">
            <div className="action-info">
              <div className="action-icon">
                <FileJson size={24} />
              </div>
              <div>
                <h4>Export Data</h4>
                <p>Download your bills, payments, and account information</p>
                {exportSummary && (
                  <div className="export-summary">
                    <span>{exportSummary.personalBills + exportSummary.householdBills} bills</span>
                    <span>•</span>
                    <span>{exportSummary.payments} payments</span>
                    <span>•</span>
                    <span>{exportSummary.personalAccounts + exportSummary.householdAccounts} accounts</span>
                  </div>
                )}
              </div>
            </div>
            <button 
              className="btn-secondary"
              onClick={() => setShowExportModal(true)}
              disabled={exportLoading}
            >
              <Download size={16} /> Export Data
            </button>
          </div>
        </div>
      </div>

      {showExportModal && (
        <div className="modal-backdrop" onClick={() => setShowExportModal(false)}>
          <div className="modal-container export-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Export Your Data</h3>
              <button className="modal-close" onClick={() => setShowExportModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="export-section">
                <h4 className="export-section-title">Export Format</h4>
                <div className="export-format-options">
                  <button 
                    className={`export-format-option ${exportFormat === 'json' ? 'selected' : ''}`}
                    onClick={() => setExportFormat('json')}
                  >
                    <div className="format-icon">
                      <FileJson size={28} />
                    </div>
                    <div className="format-info">
                      <h4>JSON</h4>
                      <p>Single file, best for backups</p>
                    </div>
                    {exportFormat === 'json' && <CheckCircle size={20} className="format-check" />}
                  </button>
                  
                  <button 
                    className={`export-format-option ${exportFormat === 'csv' ? 'selected' : ''}`}
                    onClick={() => setExportFormat('csv')}
                  >
                    <div className="format-icon">
                      <Table size={28} />
                    </div>
                    <div className="format-info">
                      <h4>CSV</h4>
                      <p>For Excel, Google Sheets</p>
                    </div>
                    {exportFormat === 'csv' && <CheckCircle size={20} className="format-check" />}
                  </button>
                </div>
              </div>
              
              <div className="export-section">
                <div className="export-section-header">
                  <h4 className="export-section-title">Data to Include</h4>
                  <div className="export-select-actions">
                    <button className="btn-text" onClick={selectAllExports}>Select All</button>
                    <span className="divider">|</span>
                    <button className="btn-text" onClick={deselectAllExports}>Clear All</button>
                  </div>
                </div>
                
                <div className="export-data-options">
                  <label className="export-data-option">
                    <input 
                      type="checkbox" 
                      checked={exportSelections.user} 
                      onChange={() => toggleExportSelection('user')}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="option-info">
                      <span className="option-label">Profile & Settings</span>
                      <span className="option-desc">Your account info and preferences</span>
                    </div>
                  </label>
                  
                  <label className="export-data-option">
                    <input 
                      type="checkbox" 
                      checked={exportSelections.bills} 
                      onChange={() => toggleExportSelection('bills')}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="option-info">
                      <span className="option-label">Bills</span>
                      <span className="option-desc">
                        {exportSummary ? `${exportSummary.personalBills + exportSummary.householdBills} bills` : 'All your bills'}
                      </span>
                    </div>
                  </label>
                  
                  <label className="export-data-option">
                    <input 
                      type="checkbox" 
                      checked={exportSelections.payments} 
                      onChange={() => toggleExportSelection('payments')}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="option-info">
                      <span className="option-label">Payment History</span>
                      <span className="option-desc">
                        {exportSummary ? `${exportSummary.payments} payment records` : 'All payment records'}
                      </span>
                    </div>
                  </label>
                  
                  <label className="export-data-option">
                    <input 
                      type="checkbox" 
                      checked={exportSelections.paymentAccounts} 
                      onChange={() => toggleExportSelection('paymentAccounts')}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="option-info">
                      <span className="option-label">Payment Accounts</span>
                      <span className="option-desc">
                        {exportSummary ? `${exportSummary.personalAccounts + exportSummary.householdAccounts} accounts` : 'All payment accounts'}
                      </span>
                    </div>
                  </label>
                  
                  <label className="export-data-option">
                    <input 
                      type="checkbox" 
                      checked={exportSelections.categories} 
                      onChange={() => toggleExportSelection('categories')}
                    />
                    <span className="checkbox-custom"></span>
                    <div className="option-info">
                      <span className="option-label">Categories</span>
                      <span className="option-desc">
                        {exportSummary ? `${exportSummary.categories} categories` : 'Bill categories'}
                      </span>
                    </div>
                  </label>
                  
                  {exportSummary?.householdBills > 0 && (
                    <label className="export-data-option">
                      <input 
                        type="checkbox" 
                        checked={exportSelections.household} 
                        onChange={() => toggleExportSelection('household')}
                      />
                      <span className="checkbox-custom"></span>
                      <div className="option-info">
                        <span className="option-label">Household Info</span>
                        <span className="option-desc">Household details and members</span>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowExportModal(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleDownloadData}
                disabled={exportLoading || !hasAnySelection}
              >
                {exportLoading ? (
                  <><Loader2 size={16} className="spinner" /> Exporting...</>
                ) : (
                  <><Download size={16} /> Download {exportFormat.toUpperCase()}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="settings-group danger-zone">
        <h3>Danger Zone</h3>
        <p className="setting-description">Irreversible actions that affect your account</p>
        
        <div className="danger-actions">
          <div className="danger-action-item">
            <div className="action-info">
              <h4>Delete Account</h4>
              <p>Permanently delete your account and all associated data</p>
            </div>
            <button 
              className="btn-danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {showDeleteConfirm && (
        <DeleteAccountModal 
          onClose={() => setShowDeleteConfirm(false)}
          onSuccess={() => {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            localStorage.removeItem('household');
            window.location.href = '/login';
          }}
        />
      )}
    </div>
  );
}

function DeleteAccountModal({ onClose, onSuccess }) {
  const { error: showError } = useToast();
  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (confirmation !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await authAPI.deleteAccount(password, confirmation);
      onSuccess();
    } catch (err) {
      console.error('Delete account error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to delete account';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container delete-account-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header danger">
          <h3><AlertTriangle size={20} /> Delete Account</h3>
          <button className="modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>
        <div className="modal-body">
          {step === 1 && (
            <>
              <div className="delete-warning-box">
                <AlertTriangle size={32} className="warning-icon" />
                <div>
                  <h4>This action is permanent</h4>
                  <p>Deleting your account will:</p>
                </div>
              </div>
              
              <ul className="delete-consequences">
                <li>Remove all your personal bills and payment history</li>
                <li>Delete your payment accounts and categories</li>
                <li>Remove your profile and all preferences</li>
                <li>Unassign you from any household bills</li>
                <li>This cannot be undone</li>
              </ul>
              
              <p className="delete-recommendation">
                <strong>Tip:</strong> You can <button className="btn-link" onClick={onClose}>download your data</button> before deleting your account.
              </p>
            </>
          )}

          {step === 2 && (
            <>
              <p className="delete-instruction">To confirm deletion, please enter your password and type <strong>DELETE</strong> below.</p>
              
              {error && (
                <div className="message message-error">
                  {error}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="delete-password">Password</label>
                <input
                  type="password"
                  id="delete-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  disabled={loading}
                  autoComplete="current-password"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="delete-confirmation">Type DELETE to confirm</label>
                <input
                  type="text"
                  id="delete-confirmation"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value.toUpperCase())}
                  placeholder="DELETE"
                  disabled={loading}
                  className={confirmation && confirmation !== 'DELETE' ? 'input-error' : ''}
                />
              </div>
            </>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          {step === 1 ? (
            <button 
              className="btn-danger"
              onClick={() => setStep(2)}
            >
              Continue
            </button>
          ) : (
            <button 
              className="btn-danger"
              onClick={handleDelete}
              disabled={loading || !password || confirmation !== 'DELETE'}
            >
              {loading ? (
                <><Loader2 size={16} className="spinner" /> Deleting...</>
              ) : (
                'Permanently Delete Account'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;